import React, { useContext } from 'react';
import { createUseStyles } from 'react-jss';
import { StaticContext } from '@sm/webassets/StaticContext';
import { t } from '@sm/intl';
import { generateMetricsAttribute } from '@sm/metrics';
import { LogoWithText, Logo } from '@sm/webassets/Logos';
import { createURL } from '@sm/utils';

import { Box } from '@wds/box';

import { CmsWrenchTheme } from '~app/styles/cmsTheme';
import { COPY } from '~app/components/SimpleLogoHeader';

export type Props = {
  utSource: string;
  utSource2: string;
  isThanksText?: boolean;
  amplitudeEvent?: string;
};

const useStyles = createUseStyles(({ type, breakpoints, spacing, brandColors }: CmsWrenchTheme) => {
  return {
    container: {
      width: '100%',
      height: '74px',
    },
    v2Container: {
      width: '100%',
      height: '100%',
      display: 'flex',
      paddingBottom: spacing[4],
      justifyContent: 'center',
      alignItems: 'center',
      [`@media (min-width: ${breakpoints.md + 1}px)`]: {
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      },
    },
    logo: {
      height: 26,
      [`@media (min-width: ${breakpoints.md}px)`]: { height: 40 },
    },
    thanksLogo: {
      fontSize: type.fontSize.cardTitle,
      fontWeight: type.fontWeight.medium,
      alignSelf: 'center',
      marginTop: -spacing[1],
      paddingRight: spacing[2],
      marginBottom: 0,
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        fontSize: (type.fontSize.cardTitle as number) + 6,
      },
    },
    logoLink: {
      lineHeight: '0rem',
      '& div span a': { color: brandColors.primary.cleanSlate },
      '& g': {
        color: brandColors.primary.cleanSlate,
        fill: brandColors.primary.cleanSlate,
      },
      '& > path': {
        color: brandColors.primary.darkGreen,
        fill: brandColors.primary.darkGreen,
      },
    },
  };
});

const BrandLogoWithText = ({
  utSource,
  utSource2,
  isThanksText = false,
  amplitudeEvent,
}: Props): React.ReactElement => {
  const classes = useStyles();
  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
  } = useContext(StaticContext);

  const metricsData = {
    amplitudeEvent,
    itemSelected: 'logo',
    templateType,
    surveyId,
    collectorId,
    respondentId,
    log: { message: `${utSource}-${utSource2}-ColoredLogoWithText-LogoWithText` },
  };

  const isThanksPage = ['rebrandEpFormIntl', 'rebrandEpForm'].includes(utSource2);

  // Main component render
  const containerClass = isThanksPage ? classes.v2Container : classes.container;
  const logoComponent = isThanksText ? (
    <Logo className={classes.thanksLogo} />
  ) : (
    <LogoWithText className={`${classes.logo} ${classes.logoLink}`} />
  );

  return (
    <Box className={containerClass}>
      <a
        aria-label={t(COPY.LOGO_ARIA_LABEL)}
        data-testid="BrandLogoWithText__LogoWithText"
        href={createURL('/', {
          ut_source: utSource,
          ut_source2: utSource2,
          ut_source3: 'BrandLogoWithText',
          ut_ctatext: 'LogoWithText',
        })}
        data-sm-metrics={generateMetricsAttribute({ data: metricsData })}
      >
        {logoComponent}
      </a>
    </Box>
  );
};

export default BrandLogoWithText;
